/*
 * Page behavior for the cart page, favorites and the pack builder.
 * Uses window.Cubica from cubica.js (cart.add/change, favorites, events).
 *
 *   <cubica-cart-page data-section-id>   qty/remove through Cubica.cart.change, promotion codes through
 *                                        cart/update.js; re-renders itself on cubica:cart:updated
 *   <cubica-favorites-list>              renders saved handles via /products/<handle>?section_id=cubica-favorites
 *   <cubica-pack-builder>                collects N picks, then Cubica.cart.add for every variant
 */
(() => {
  const C = window.Cubica || {};
  const define = (name, element) => customElements.get(name) || customElements.define(name, element);
  const sectionHtml = async (url, id) => {
    const response = await fetch(url);
    if (!response.ok) return null;
    return new DOMParser().parseFromString(await response.text(), 'text/html').getElementById('shopify-section-' + id);
  };

  define(
    'cubica-cart-page',
    class extends HTMLElement {
      connectedCallback() {
        const change = async (control, quantity) => {
          const line = control.closest('[data-line-key]');
          if (!line || control.getAttribute('aria-busy') === 'true') return;
          control.setAttribute('aria-busy', 'true');
          if (control.tagName === 'SELECT') control.disabled = true;
          try {
            await C.cart.change(line.dataset.lineKey, quantity);
          } catch (error) {
            control.removeAttribute('aria-busy');
            control.disabled = false;
            const status = line.querySelector('[data-status]');
            if (status) status.textContent = error.message;
          }
        };
        this.addEventListener('click', (event) => {
          const remove = event.target.closest('[data-cart-qty]');
          if (remove) return change(remove, Number(remove.dataset.cartQty));
          const toggle = event.target.closest('.c-cart__discount-toggle');
          if (toggle) {
            const row = document.getElementById(toggle.getAttribute('aria-controls'));
            row.hidden = !row.hidden;
            toggle.setAttribute('aria-expanded', !row.hidden);
            if (!row.hidden) row.querySelector('input')?.focus();
            return;
          }
          const code = event.target.closest('[data-remove-code]');
          if (code) this.codes(this.applied().filter((c) => c !== code.dataset.removeCode));
        });
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-cart-select]')) change(event.target, Number(event.target.value));
        });
        this.addEventListener('submit', (event) => {
          if (!event.target.matches('[data-cart-discount]')) return;
          event.preventDefault();
          const code = event.target.code.value.trim();
          if (code) this.codes([...this.applied(), code], code);
        });
        this.onUpdate = () => this.refresh();
        document.addEventListener('cubica:cart:updated', this.onUpdate);
      }
      disconnectedCallback() {
        document.removeEventListener('cubica:cart:updated', this.onUpdate);
      }
      applied() {
        return (this.querySelector('[data-cart-discount]')?.dataset.codes || '').split(',').filter(Boolean);
      }
      // Promotion codes through the Ajax Cart API (cart/update.js "discount": every code, comma-separated).
      async codes(list, added) {
        const form = this.querySelector('[data-cart-discount]');
        form?.querySelector('[type=submit]')?.setAttribute('aria-busy', 'true');
        let error = '';
        const update = (codes) =>
          fetch(C.routes.cart + '/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: [...new Set(codes)].join(',') }),
          });
        try {
          const response = await update(list);
          const cart = await response.json();
          if (!response.ok) error = cart.description || C.strings.addError;
          else if (added && !(cart.discount_codes || []).some((d) => d.code.toLowerCase() === added.toLowerCase() && d.applicable)) {
            error = C.strings.codeError;
            await update(list.filter((c) => c !== added)); // the cart keeps only codes that apply
          }
        } catch {
          error = C.strings.addError;
        }
        this.error = error;
        await C.cart.refresh();
      }
      async refresh() {
        const id = this.dataset.sectionId;
        const focused = document.activeElement?.closest('[data-line-key]')?.dataset.lineKey;
        const next = await sectionHtml(`${C.routes.cart}?section_id=${id}`, id).catch(() => null);
        const host = document.getElementById('shopify-section-' + id);
        if (!next || !host) return;
        host.replaceChildren(...next.childNodes);
        if (focused) host.querySelector(`[data-line-key="${CSS.escape(focused)}"] [data-cart-select]`)?.focus();
        if (this.error) {
          const row = host.querySelector('.c-cart__discount-row');
          host.querySelector('.c-cart__discount-toggle')?.setAttribute('aria-expanded', 'true');
          if (row) row.hidden = false;
          const status = host.querySelector('[data-discount-error]');
          if (status) status.textContent = this.error;
          this.error = '';
        }
      }
    }
  );

  define(
    'cubica-favorites-list',
    class extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-grid]');
        this.empty = this.querySelector('[data-empty]');
        this.root = (C.routes.root || '/').replace(/\/?$/, '/');
        this.render();
        this.onChange = () => this.render();
        document.addEventListener('cubica:favorites:changed', this.onChange);
      }
      disconnectedCallback() {
        document.removeEventListener('cubica:favorites:changed', this.onChange);
      }
      async render() {
        const handles = C.favorites.list();
        const token = (this.token = Symbol());
        const cards = await Promise.all(
          handles.map((handle) =>
            sectionHtml(`${this.root}products/${encodeURIComponent(handle)}?section_id=cubica-favorites`, 'cubica-favorites').catch(() => null)
          )
        );
        if (token !== this.token) return;
        const items = cards.filter(Boolean).flatMap((section) => [...section.querySelectorAll('[data-favorite-card]')]);
        const inBag = (document.querySelector('#shopify-section-cubica-bag .c-bag__content')?.dataset.products || '').split(',');
        items.forEach((item) => {
          const card = item.querySelector('.c-product-card');
          card?.classList.toggle('is-in-bag', inBag.includes(card.dataset.productId));
        });
        this.grid.replaceChildren(...items);
        this.grid.hidden = !items.length;
        this.empty.hidden = items.length > 0;
        this.removeAttribute('aria-busy');
      }
    }
  );

  define(
    'cubica-pack-builder',
    class extends HTMLElement {
      connectedCallback() {
        this.picks = [];
        this.size = Number(this.dataset.size);
        this.currency = this.dataset.currency;
        this.number = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 });
        this.addEventListener('click', (event) => {
          const add = event.target.closest('[data-add]');
          const remove = event.target.closest('[data-remove]');
          if (add) this.add(add);
          if (remove) {
            this.picks.splice(Number(remove.dataset.remove), 1);
            this.render();
          }
          if (event.target.closest('[data-submit]')) this.submit();
        });
      }
      money(cents) {
        return this.number.format(Math.round(cents / 100)) + ' ' + this.currency;
      }
      say(text) {
        this.querySelector('[data-status]').textContent = text;
      }
      add(button) {
        if (this.picks.length >= this.size) return this.say(this.dataset.full);
        const option = button.closest('[data-pick]').querySelector('select').selectedOptions[0];
        if (!option || option.disabled) return this.say(this.dataset.soldOut);
        this.picks.push({ id: option.value, price: Number(option.dataset.price), title: `${button.dataset.title} · ${option.textContent.trim()}` });
        this.render();
        this.say(this.dataset.added.replace('[title]', this.picks.at(-1).title));
      }
      render() {
        const list = this.querySelector('[data-list]');
        list.replaceChildren(
          ...this.picks.map((pick, index) => {
            const li = document.createElement('li');
            const name = document.createElement('span');
            const remove = document.createElement('button');
            name.textContent = pick.title;
            remove.type = 'button';
            remove.className = 'c-btn c-btn--link t-subhead';
            remove.dataset.remove = index;
            remove.textContent = this.dataset.removeLabel;
            remove.setAttribute('aria-label', this.dataset.removeItem.replace('[title]', pick.title));
            li.append(name, remove);
            return li;
          })
        );
        this.querySelector('[data-count]').textContent = this.picks.length;
        const bar = this.querySelector('.c-progress > span');
        if (bar) bar.style.transform = `scaleX(${this.picks.length / this.size})`;
        const full = this.picks.length === this.size;
        const total = this.picks.reduce((sum, pick) => sum + pick.price, 0);
        const packTotal = Math.round(total * (1 - Number(this.dataset.percent) / 100));
        const node = this.querySelector('[data-total]');
        if (!this.picks.length) node.textContent = '';
        else if (full && packTotal < total)
          node.innerHTML = `<s class="u-muted">${this.money(total)}</s> <strong>${this.money(packTotal)}</strong> · ${this.dataset.save.replace('[amount]', this.money(total - packTotal))}`;
        else {
          const left = this.size - this.picks.length;
          node.textContent = `${this.money(total)} · ${left === 1 ? this.dataset.leftOne : this.dataset.leftOther.replace('[count]', left)}`;
        }
        this.querySelector('[data-submit]').disabled = !full;
      }
      async submit() {
        const quantities = new Map();
        this.picks.forEach((pick) => quantities.set(pick.id, (quantities.get(pick.id) || 0) + 1));
        const button = this.querySelector('[data-submit]');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        this.say(this.dataset.adding);
        try {
          for (const [id, quantity] of quantities) await C.cart.add(id, quantity);
          this.picks = [];
          this.render();
          this.say(this.dataset.done);
        } catch (error) {
          button.disabled = false;
          this.say(`${error.message || this.dataset.error} ${this.dataset.retry}`);
        } finally {
          button.removeAttribute('aria-busy');
        }
      }
    }
  );
})();
